// Store
import { store } from "../../redux";
import { setRequestState } from "../../redux/appreadouts/actions";
import {
  IError,
  ITranslateKeyError,
} from "../../interfaces/general.interfaces";
// Interfaces
import { IErrorCodeOptions, IMiddlewareOptions } from "./interfaces";
// Helpers
import { isResponseStatusHandled, redirectToSystemError } from "./helpers";
// Config
import { staticConfig } from "../../config/static";
import { getRefreshToken } from "../refreshToken.service";
import { authTokenStore } from "../auth/tokenStore";

const settings = {
  apiBaseUrl: staticConfig.apiBaseUrl,
};

// Binary bodies must not be read with response.text(): that corrupts the file.
const BINARY_RESPONSE_TYPES = [
  "zip",
  "octet-stream",
  "pdf",
  "image/",
  "application/vnd",
];

const AUTHORIZATION_HEADER = "Authorization";

// Stable auth error codes emitted by the backend (app/common/auth_errors.py).
// Branch on these instead of matching the human-readable `detail` prose.
const AUTH_CODE = {
  ACCESS_TOKEN_EXPIRED: "ACCESS_TOKEN_EXPIRED",
  NOT_ENOUGH_PERMISSIONS: "NOT_ENOUGH_PERMISSIONS",
  REFRESH_TOKEN_REVOKED: "REFRESH_TOKEN_REVOKED",
  REFRESH_TOKEN_MISSING: "REFRESH_TOKEN_MISSING",
  REFRESH_TOKEN_INVALID: "REFRESH_TOKEN_INVALID",
} as const;

const REFRESH_FAILURE_CODES: string[] = [
  AUTH_CODE.REFRESH_TOKEN_REVOKED,
  AUTH_CODE.REFRESH_TOKEN_MISSING,
  AUTH_CODE.REFRESH_TOKEN_INVALID,
];

// Legacy fallback: older backend builds return only prose in `detail`. Kept so the
// frontend keeps working during rollout; remove once the coded backend is deployed.
const LEGACY_TOKEN_EXPIRED_PATTERNS = [
  "access token expired",
  "access token has expired",
  "access token has expired.",
  "access token has expired. get a new one",
];

let refreshPromise: Promise<boolean> | null = null;

const normalizeHeaders = (headers?: HeadersInit): Headers => {
  if (headers instanceof Headers) {
    return new Headers(headers);
  }

  const normalized = new Headers();

  if (Array.isArray(headers)) {
    headers.forEach(([key, value]) => normalized.append(key, value));
    return normalized;
  }

  if (headers && typeof headers === "object") {
    Object.entries(headers).forEach(([key, value]) => {
      if (typeof value !== "undefined") {
        normalized.append(key, String(value));
      }
    });
  }

  return normalized;
};

const applyDefaultHeaders = (options: RequestInit, skipAuthHeader?: boolean): RequestInit => {
  const headers = normalizeHeaders(options.headers);
  if (!skipAuthHeader) {
    const authHeader = authTokenStore.getAuthHeader();
    if (authHeader.Authorization) {
      headers.set(AUTHORIZATION_HEADER, authHeader.Authorization);
    }
  }
  return {
    ...options,
    headers,
  };
};

const stripAuthorizationHeader = (options: RequestInit): RequestInit => {
  if (!options.headers) {
    return options;
  }
  const headers = normalizeHeaders(options.headers);
  headers.delete(AUTHORIZATION_HEADER);
  return {
    ...options,
    headers,
  };
};

const clearSession = () => {
  authTokenStore.clearTokens();
  try {
    localStorage.removeItem("isLoggedIn");
    localStorage.removeItem("customer_id");
    localStorage.removeItem("customer_ids");
    localStorage.removeItem("user_id");
    localStorage.removeItem("environment_id");
  } catch {
    // ignore storage errors
  }
};

const redirectToLogin = () => {
  if (typeof window !== "undefined") {
    window.location.href = "/";
  }
};

const handleRefreshFailure = () => {
  clearSession();
  redirectToLogin();
};

const refreshAccessToken = async (): Promise<boolean> => {
  if (refreshPromise) {
    return refreshPromise;
  }

  const refreshToken = authTokenStore.getRefreshToken();
  const email = authTokenStore.getEmail();

  if (!refreshToken || !email) {
    return false;
  }

  refreshPromise = getRefreshToken(refreshToken, email)
    .then((res) => {
      authTokenStore.setTokens({
        accessToken: res.access_token,
        refreshToken: res.refresh_token,
      });
      return true;
    })
    .catch((error) => {
      const code: string | undefined = error?.resBody?.code;
      const legacyRefreshDetail =
        typeof error?.resBody?.detail === "string" &&
        error.resBody.detail.toLowerCase().includes("refresh token");
      const shouldLogout =
        error?.status === 401 ||
        (code ? REFRESH_FAILURE_CODES.includes(code) : legacyRefreshDetail);
      if (shouldLogout) {
        handleRefreshFailure();
      }
      return false;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
};

const shouldRetryWithRefresh = (
  response: Response,
  resBody: any,
  hasRetried: boolean
): boolean => {
  if (hasRetried || response.status !== 401) {
    return false;
  }

  if (!authTokenStore.getRefreshToken()) {
    return false;
  }

  // Preferred path: branch on the stable backend error code.
  const code: string | undefined = resBody?.code;
  if (code) {
    return code === AUTH_CODE.ACCESS_TOKEN_EXPIRED;
  }

  // Legacy fallback for backends that only return prose in `detail`.
  const detail =
    typeof resBody?.detail === "string" ? resBody.detail.toLowerCase() : "";

  if (detail === "not enough permissions") {
    return false;
  }

  if (!detail) {
    return true;
  }

  return LEGACY_TOKEN_EXPIRED_PATTERNS.some((pattern) =>
    detail.includes(pattern)
  );
};


export const typedRequest = <T>(
  url: string,
  options: RequestInit = {},
  middlewareOptions?: IMiddlewareOptions
): Promise<T> =>
  new Promise(async (resolve, reject) => {
    const isUrlExclusive = middlewareOptions?.options?.isUrlExclusive;
    const skipAuthHeader = middlewareOptions?.options?.skipAuthHeader;
    const errorCodeOptions = middlewareOptions?.errorCodeOptions;
    const fetchUrl = isUrlExclusive ? url : settings.apiBaseUrl + url;

    try {
      store.dispatch(setRequestState(true));
      await executeFetchRequest(
        fetchUrl,
        options,
        errorCodeOptions,
        resolve,
        reject,
        false,
        skipAuthHeader
      );
    } catch (error) {
      reject(error);
    } finally {
      store.dispatch(setRequestState(false));
    }
  });

const executeFetchRequest = async <T>(
  fetchUrl: string,
  options: RequestInit,
  errorCodeOptions: IErrorCodeOptions | undefined,
  resolve: (value: T | PromiseLike<T>) => void,
  reject: (reason?: any) => void,
  hasRetried = false,
  skipAuthHeader?: boolean
): Promise<void> => {
  const response = await fetch(fetchUrl, applyDefaultHeaders(options, skipAuthHeader));
  const responseStatusHandled = isResponseStatusHandled(
    response,
    errorCodeOptions
  );

  if (
    !response.ok &&
    ![400, 401, 403, 404, 409, 422].includes(response.status) &&
    !responseStatusHandled
  ) {
    redirectToSystemError(response, null);
    reject();
  }

  const responseType = response.headers.get("content-type");

  const isBinary = BINARY_RESPONSE_TYPES.some((type) =>
    (responseType ?? "").toLowerCase().includes(type)
  );

  let rawResponse: any;
  if (isBinary) {
    rawResponse = await response.blob();
  } else {
    rawResponse = await response.text();
  }
  const responseBody = parseRawResponse(rawResponse);

  if (
    !response.ok &&
    shouldRetryWithRefresh(response, responseBody, hasRetried)
  ) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      const retryOptions = stripAuthorizationHeader(options);
      await executeFetchRequest(
        fetchUrl,
        retryOptions,
        errorCodeOptions,
        resolve,
        reject,
        true,
        skipAuthHeader
      );
      return;
    }
  }

  if (!response.ok) {
    resolveBody(
      resolve,
      reject,
      responseBody,
      responseStatusHandled,
      response,
      errorCodeOptions
    );
    return;
  }

  resolveBody(
    resolve,
    reject,
    responseBody,
    responseStatusHandled,
    response,
    errorCodeOptions
  );
};

const parseRawResponse = (rawResponse: string): any => {
  try {
    return JSON.parse(rawResponse);
  } catch {
    return rawResponse;
  }
};

const resolveBody = <T>(
  resolve: (value: T | PromiseLike<T>) => void,
  reject: (reason?: any) => void,
  resBody: any,
  responseStatusHandled: boolean,
  response: Response,
  errorCodeOptions?: IErrorCodeOptions
): void => {
  const errorList: Array<IError | ITranslateKeyError> = [];

  if (
    (resBody.validationErrors && resBody.validationErrors.length > 0) ||
    resBody.errorCode
  ) {
    if (resBody.validationErrors) {
      // The validation error logic goes here
    } else if (resBody.errorCode) {
      // The errorcode logic goes here
    }
    reject(errorList);
  } else if (resBody.error_description) {
    reject(resBody);

    return;
  } else {
    if (response.ok) {
      resolve(resBody);

      return;
    }

    if (responseStatusHandled) {
      reject({
        status: response.status,
        body: resBody,
      });

      return;
    }

    if (response.status === 401) {
      reject({
        status: response.status,
        body: response.body,
        resBody: resBody,
      });

      return;
    }

    if (response.status === 403) {
      // add errorlist here
      reject(errorList);

      return;
    }

    if (response.status === 400) {
      reject({
        status: response.status,
        body: response.body,
        resBody: resBody,
      });

      return;
    }

    if (response.status === 409) {
      reject({
        status: response.status,
        body: response.body,
        resBody: resBody,
      });

      return;
    }

    if (response.status === 422) {
      reject({
        status: response.status,
        body: response.body,
        resBody: resBody,
      });

      return;
    }

    if (response.status === 404) {
      reject({
        status: response.status,
        body: response.body,
        resBody: resBody,
      });

      return;
    }

    redirectToSystemError(response, null);
  }
};
